import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { TbArrowBackUp } from "react-icons/tb";
import { FaFolder } from "react-icons/fa";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import InfiniteScroll from "../components/InfiniteScroll";
import NotFound from "../components/NotFound";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import apiClient from "../api/apiClient";
import { handleErrors } from "../modules/handleErrors";
import { foldersApi } from "../api/shopApi";
import { Modal } from "react-bootstrap";
import ModalPublishUnPublish from "../modals/PublishUnPublishModal";
import WarningModal from "../modals/WarningModal";
import { SearchBar } from "./MyListings/components/SearchBar";
import { ViewModeToggle } from "./MyListings/components/ViewModeToggle";
import { SortDropdown } from "./MyListings/components/SortDropdown";
import { GridListingCard } from "./MyListings/components/GridListingCard";
import { DetailedListingCard } from "./MyListings/components/DetailedListingCard";
import { isBundleListing, markAsBundle } from "./MyListings/utils/helpers";
import { ListingModal, BundleModal } from "./MyListings/components/ListingModals";
import { DeleteModal } from "./MyListings/components/DeleteModal";
import { BatchPriceModal } from "./MyListings/components/BatchPriceModal";
import { BundleListingsView } from "./MyListings/components/BundleListingsView";
import { useBundleDetail } from "./MyListings/hooks/useBundleDetail";
import { useSharableLink } from "./MyListings/hooks/useSharableLink";
import { useGuardedAsync } from "../hooks/useGuardedAsync";
import { Loader } from "../components/Loader/loader";

const MY_LISTINGS_VIEW_MODE_KEY = "mockee_my_listings_view_mode";

function getInitialViewMode() {
  if (typeof window === "undefined") return "grid";
  try {
    const v = window.localStorage.getItem(MY_LISTINGS_VIEW_MODE_KEY);
    if (v === "grid" || v === "detailed") return v;
  } catch {
    /* ignore */
  }
  return "grid";
}

const MyListings = () => {
  const { userid } = useSelector((state) => state.auth);
  const location = useLocation();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  // State management
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState(getInitialViewMode);
  const [detailedSortBy, setDetailedSortBy] = useState("-created_at");
  const [editlistdata, setEditlistdata] = useState();
  const [mocko_id, setMocko_id] = useState(null);
  const [show, setShow] = useState(false);
  const [bundleShow, setBundleShow] = useState(false);
  const [bundleEditLoading, setBundleEditLoading] = useState(false);
  const [showBundleListings, setShowBundleListings] = useState(false);
  const [bundleListingsId, setBundleListingsId] = useState(null);
  const [showRemove, setShowRemove] = useState(false);
  const [isBundleRemove, setIsBundleRemove] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isBundle, setIsBundle] = useState(false);
  const [publishUnPublishMocko_id, setPublishUnPublishMocko_id] = useState(null);
  const [publishValue, setPublishValue] = useState(null);
  const [isUnblockRequest, setIsUnblockRequest] = useState(null);
  const [isSharableLinkRequest, setIsSharableLinkRequest] = useState(null);
  const [isSharableLinkEnabled, setIsSharableLinkEnabled] = useState(false);
  const [selectedListingIds, setSelectedListingIds] = useState([]);
  const [selectedBundleIds, setSelectedBundleIds] = useState([]);
  const [showBatchPriceModal, setShowBatchPriceModal] = useState(false);
  const [selectionModeActive, setSelectionModeActive] = useState(false);
  const [shopFolders, setShopFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [showDeleteFolderWarning, setShowDeleteFolderWarning] = useState(false);
  const [isEditingFolderName, setIsEditingFolderName] = useState(false);
  const [editingFolderName, setEditingFolderName] = useState("");
  const [renamingFolder, setRenamingFolder] = useState(false);
  const [showMoveToFolderModal, setShowMoveToFolderModal] = useState(false);
  const [moveModalBatchMode, setMoveModalBatchMode] = useState(false);
  const [selectedListingForMove, setSelectedListingForMove] = useState({ id: null, isBundle: false });
  /** undefined = none selected, "root" = My Listings root, number = folder id */
  const [selectedMoveDestination, setSelectedMoveDestination] = useState(undefined);
  const [mockupsCount, setMockupsCount] = useState(0);
  const { loading: moveToFolderLoading, run: runMoveToFolder } = useGuardedAsync();
  const [isSelectedAll, setIsSelectedAll] = useState(false);

  const resetBatchSelection = useCallback(() => {
    setSelectionModeActive(false);
    setSelectedListingIds([]);
    setSelectedBundleIds([]);
    setIsSelectedAll(false);
    setMoveModalBatchMode(false);
  }, []);

  /** Fresh entry to this page / back-forward cache restore — batch selection must not persist. */
  useEffect(() => {
    resetBatchSelection();
  }, [location.pathname, resetBatchSelection]);

  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) {
        resetBatchSelection();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [resetBatchSelection]);

  // Fetch function for grid view when viewing a specific folder (listings + bundles in that folder)
  const fetchFolderGridData = useMemo(() => {
    return async (page) => {
      const listParams = new URLSearchParams();
      listParams.append("user_id", userid);
      listParams.append("shop_folder", currentFolderId);
      if (searchQuery) listParams.append("search", searchQuery);
      listParams.append("page", page);
      listParams.append("page_size", "10");
      const bundleParams = new URLSearchParams();
      bundleParams.append("shop_folder", currentFolderId);
      if (searchQuery) bundleParams.append("search", searchQuery);
      bundleParams.append("page", page);
      bundleParams.append("page_size", "10");
      const [listRes, bundleRes] = await Promise.all([
        apiClient.get(`/listings/?${listParams.toString()}`),
        apiClient.get(`/bundle-listings/?user_id=${userid}&calledFrom=home&${bundleParams.toString()}`),
      ]);
      const listings = listRes?.ok ? listRes.data.results || [] : [];
      const bundles = (bundleRes?.data?.results || []).map(markAsBundle);
      const combined = [...bundles, ...listings].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      const listCount = listRes?.data?.count || 0;
      const bundleCount = bundleRes?.data?.count || 0;
      return {
        ok: listRes?.ok || bundleRes?.ok,
        data: {
          results: combined,
          count: listCount + bundleCount,
        },
      };
    };
  }, [userid, currentFolderId, searchQuery]);

  
  // Fetch function for grid view infinite scroll (combines listings and bundles) - root "All" view
  const fetchCombinedGridData = useMemo(() => {
    return async (page) => {
      const params = new URLSearchParams();
      if (searchQuery) {
        params.append("search", searchQuery);
      }
      params.append("page", page);
      params.append("page_size", "10");
      params.append("shop_folder__isnull", "true");

      const [featuredRes, bundleRes] = await Promise.all([
        apiClient.get(`/listings/?user_id=${userid}&${params.toString()}`),
        apiClient.get(`/bundle-listings/?user_id=${userid}&calledFrom=home&${params.toString()}`)
      ]);

      const mockups = featuredRes?.ok ? featuredRes.data.results || [] : [];
      const bundles = (bundleRes?.data?.results || []).map(markAsBundle);
      
      // Combine and sort by created_at
      const combined = [...bundles, ...mockups].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setMockupsCount(featuredRes?.data?.count || 0);
      return {
        ok: featuredRes?.ok || bundleRes?.ok,
        data: {
          results: combined,
          count: featuredRes?.data?.count || 0
        }
      };
    };
  }, [userid, searchQuery]);

  // Grid fetch: when inside a folder show only that folder's listings; otherwise show all (listings + bundles)
  const fetchGridFunction = currentFolderId ? fetchFolderGridData : fetchCombinedGridData;

  // Respect selected view mode both at root and inside folders
  const showGridView = viewMode === "grid";
  const showDetailedView = viewMode === "detailed";

  // Infinite scroll for grid view (listings + bundles) — not reset by tab switches
  const { data: gridRecords, loading: gridLoader, hasMore: gridHasMore, loadMore: gridLoadMore, refetch: refetchGrid } = useInfiniteScroll(
    fetchGridFunction,
    [searchQuery, currentFolderId],
    10,
    showGridView
  );

  // Fetch function for detailed view at root (sales stats)
  const fetchCombinedDetailedData = useMemo(() => {
    return async (page) => {
      const params = new URLSearchParams();
      if (searchQuery) {
        params.append("search", searchQuery);
      }
      params.append("page", page);
      params.append("page_size", "10");
      params.append("shop_folder__isnull", "true");

      
      if (detailedSortBy) {
        params.append("ordering", detailedSortBy);
        params.append("sort_by", detailedSortBy);
      }

      const [mockupSalesRes, bundleSalesRes] = await Promise.all([
        apiClient.get(`/sales/mockup-sales/?user_id=${userid}&${params.toString()}`),
        apiClient.get(`/sales/bundle-sales/?user_id=${userid}&${params.toString()}`)
      ]);

      const mockupSales = mockupSalesRes?.ok ? mockupSalesRes.data.results || [] : [];
      const bundleSales = (bundleSalesRes?.ok ? bundleSalesRes.data.results || [] : []).map(
        markAsBundle
      );
      
      // Combine results
      const combined = [...bundleSales, ...mockupSales];

      // Sort combined results based on detailedSortBy
      const sortByRaw = detailedSortBy || "-created_at";
      const desc = sortByRaw.startsWith("-");
      const sortKey = sortByRaw.replace(/^-/, "");

      const keyMapping = {
        total_sales: "totalSales",
        total_sales_this_month: "totalSalesThisMonth",
        price: "price",
        total_earned: "totalEarned",
        total_earned_this_month: "totalEarnedThisMonth",
        created_at: "created_at",
      };

      const mappedKey = keyMapping[sortKey] || sortKey;

      const getComparableValue = (item, key) => {
        const val = item?.[key];
        if (key === "created_at") {
          if (!val) return 0;
          const t = new Date(val).getTime();
          return isNaN(t) ? 0 : t;
        }
        if (val === undefined || val === null) return 0;
        if (typeof val === "number") return val;
        const parsed = parseFloat(val);
        if (!isNaN(parsed)) return parsed;
        return String(val).toLowerCase();
      };

      const comparator = (a, b) => {
        const A = getComparableValue(a, mappedKey);
        const B = getComparableValue(b, mappedKey);
        if (typeof A === "string" || typeof B === "string") {
          const aStr = String(A || "");
          const bStr = String(B || "");
          return desc ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr);
        }
        if (A === B) return 0;
        return desc ? B - A : A - B;
      };

      const sorted = combined.sort(comparator);

      return {
        ok: mockupSalesRes?.ok || bundleSalesRes?.ok,
        data: {
          results: sorted,
          count: mockupSalesRes?.data?.count || bundleSalesRes?.data?.count || 0
        }
      };
    };
  }, [userid, searchQuery, detailedSortBy]);

  // Inside folders, reuse folder listings/bundles data for detailed cards
  const fetchDetailedFunction = currentFolderId
    ? fetchFolderGridData
    : fetchCombinedDetailedData;

  // Detailed view only while detailed tab is active; cached across switches back to grid
  const { data: detailedRecords, loading: detailedLoader, hasMore: detailedHasMore, loadMore: detailedLoadMore, refetch: refetchDetailed } = useInfiniteScroll(
    fetchDetailedFunction,
    [searchQuery, detailedSortBy, currentFolderId],
    10,
    showDetailedView
  );

  const records = showGridView ? gridRecords : [];
  const loader = showGridView ? gridLoader : detailedLoader;
  const currentListingRecords = showGridView ? records : detailedRecords;
  const gridHasMoreVal = showGridView ? gridHasMore : false;
  const gridLoadMoreVal = showGridView ? gridLoadMore : () => {};
  const gridLoaderVal = showGridView ? gridLoader : false;
  const currentMockupIds = (currentListingRecords || [])
    .filter((item) => !isBundleListing(item))
    .map((item) => item.id);

  const selectAllActive = useMemo(() => {
    if (!selectionModeActive) return false;
    if (currentFolderId) {
      const ids = currentMockupIds || [];
      if (!ids.length) return false;
      return (
        selectedBundleIds.length === 0 &&
        selectedListingIds.length === ids.length &&
        ids.every((id) => selectedListingIds.includes(id))
      );
    }
    return isSelectedAll;
  }, [
    selectionModeActive,
    currentFolderId,
    currentMockupIds,
    selectedListingIds,
    selectedBundleIds,
    isSelectedAll,
  ]);

  const { bundleListingsById } = useBundleDetail(bundleListingsId);
  const { handleSharableLink } = useSharableLink();

  const loadFolders = () => {
    if (!userid) return;
    foldersApi.listMine(userid).then((list) => setShopFolders(Array.isArray(list) ? list : []));
  };

  useEffect(() => {
    loadFolders();
  }, [userid]);

  useEffect(() => {
    try {
      window.localStorage.setItem(MY_LISTINGS_VIEW_MODE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  // Handlers
  const handleApplySearch = (value) => {
    setSearchQuery(value);
  };

  // Refetch helpers for modals — reset both caches so the inactive tab reloads fresh when opened
  const refreshListings = () => {
    refetchGrid();
    refetchDetailed();
  };
  const fetchFeaturedMockups = refreshListings;
  const fetchBundleMockups = refreshListings;

  const handleModal = () => {
    setMocko_id("");
    setEditlistdata("");
    setShow(true);
  };

  const handleBundleModal = () => {
    setMocko_id("");
    setEditlistdata("");
    setBundleShow(true);
  };

  const handleEdit = (item) => {
    handleModal();
    setEditlistdata(item);
    setMocko_id(item?.id);
  };

  const handleBundleEdit = async (item) => {
    const id = item?.id;
    if (!id || bundleEditLoading) return;

    setBundleEditLoading(true);
    try {
      const result = await apiClient.get(`/bundle-listings/${id}/`);
      setEditlistdata(result.ok ? result.data : item);
    } catch {
      setEditlistdata(item);
    } finally {
      setBundleEditLoading(false);
    }
    setMocko_id(id);
    setBundleShow(true);
  };

  const handleEditItem = (item) => {
    if (isBundleListing(item)) {
      handleBundleEdit(item);
    } else {
      handleEdit(item);
    }
  };

  const openDeleteModal = (id, isBundle = false) => {
    setMocko_id(id);
    setShowRemove(true);
    setIsBundleRemove(isBundle);
  };

  const openPublishModal = (
    id,
    value,
    unblockRequest,
    item,
    enableDisableLinkRequest,
    isSharableLinkEnabled
  ) => {
    setIsBundle(isBundleListing(item));

    setPublishUnPublishMocko_id(id);
    setShowPublishModal(true);
    setPublishValue(value);
    setIsUnblockRequest(unblockRequest || false);
    setIsSharableLinkRequest(enableDisableLinkRequest || false);
    setIsSharableLinkEnabled(isSharableLinkEnabled);
    handleDropDownClose();
  };

  const handleDropDownClick = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setSelectedItemId(item.id);
  };

  const handleDropDownClose = () => {
    setMenuAnchor(null);
    setSelectedItemId(null);
  };

  const handleOpenBundle = (id) => {
    setBundleListingsId(id);
    setShowBundleListings(true);
  };

  const handleToggleSelect = (id, isBundle) => {
    if (isBundle) {
      setSelectedBundleIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    } else {
      setIsSelectedAll(false);
      setSelectedListingIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    }
  };

  const handleBatchPriceSuccess = () => {
    setSelectedListingIds([]);
    setSelectedBundleIds([]);
    setIsSelectedAll(false);
    refreshListings();
  };

  const handleSelectButtonClick = () => {
    if (selectedListingIds.length > 0 || selectedBundleIds.length > 0 || isSelectedAll) {
      setSelectedListingIds([]);
      setSelectedBundleIds([]);
      setIsSelectedAll(false);
    } else {
      setSelectionModeActive((prev) => !prev);
    }
  };

  const handleSelectAll = () => {
    if (currentFolderId) {
      setSelectedListingIds([...currentMockupIds]);
      setSelectedBundleIds([]);
      setIsSelectedAll(false);
    } else {
      setIsSelectedAll(true);
      setSelectedListingIds([]);
      setSelectedBundleIds([]);
    }
  };

  const handleUnselectAll = () => {
    setSelectedListingIds([]);
    setSelectedBundleIds([]);
    setIsSelectedAll(false);
  };

  const handleFolderChange = async (listingId, folderId, isBundle) => {
    if (isBundle) return;
    try {
      await apiClient.patch(`/listings/${listingId}/`, { shop_folder: folderId || null });
      refreshListings();
    } catch (e) {
      console.error("Failed to update folder", e);
    }
  };

  const removeFromFolder = async (id, isBundle) => {
    try {
      if (isBundle) {
        await apiClient.patch(`/bundle-listings/${id}/`, { shop_folder: null });
        toast.success("Bundle removed from folder");
      } else {
        await apiClient.patch(`/listings/${id}/`, { shop_folder: null });
        toast.success("Listing removed from folder");
      }
      refetchGrid();
    } catch (e) {
      console.error("Failed to remove from folder", e);
      toast.error("Failed to remove from folder");
    }
  };

  const handleMoveToFolder = (id, isBundle) => {
    setMoveModalBatchMode(false);
    setSelectedListingForMove({ id, isBundle: !!isBundle });
    setSelectedMoveDestination(undefined);
    setShowMoveToFolderModal(true);
  };

  const closeMoveToFolderModal = () => {
    if (moveToFolderLoading) return;
    setShowMoveToFolderModal(false);
    setMoveModalBatchMode(false);
    setSelectedListingForMove({ id: null, isBundle: false });
    setSelectedMoveDestination(undefined);
  };

  const handleConfirmMoveToFolder = () => {
    if (selectedMoveDestination === undefined) return;

    const folderId =
      selectedMoveDestination === "root" ? null : selectedMoveDestination;

    if (moveModalBatchMode) {
      batchMoveToFolder(folderId);
      return;
    }

    if (selectedListingForMove.isBundle) {
      moveBundleToFolder(selectedListingForMove.id, folderId);
    } else {
      moveListingToFolder(selectedListingForMove.id, folderId);
    }
  };

  const resetMoveToFolderModalState = () => {
    setShowMoveToFolderModal(false);
    setMoveModalBatchMode(false);
    setSelectedListingForMove({ id: null, isBundle: false });
    setSelectedMoveDestination(undefined);
  };

  const moveListingToFolder = (listingId, folderId) => {
    runMoveToFolder(async () => {
      try {
        await apiClient.patch(`/listings/${listingId}/`, { shop_folder: folderId });
        toast.success(folderId == null ? "Listing moved to root" : "Listing moved to folder");
        resetMoveToFolderModalState();
        refreshListings();
      } catch (e) {
        console.error("Failed to move to folder", e);
        toast.error("Failed to move to folder");
      }
    });
  };

  const moveBundleToFolder = (bundleId, folderId) => {
    runMoveToFolder(async () => {
      try {
        await apiClient.patch(`/bundle-listings/${bundleId}/`, { shop_folder: folderId });
        toast.success(folderId == null ? "Bundle moved to root" : "Bundle moved to folder");
        resetMoveToFolderModalState();
        refreshListings();
      } catch (e) {
        console.error("Failed to move bundle to folder", e);
        toast.error("Failed to move bundle to folder");
      }
    });
  };

  const batchMoveToFolder = (folderId) => {
    runMoveToFolder(async () => {
      const listingIds = isSelectedAll ? [] : selectedListingIds.map((id) => String(id));
      const bundleIds = selectedBundleIds.map((id) => String(id));
      const body = {
        shop_folder: folderId,
        ...(isSelectedAll ? { isSelectedAll: true } : { ids: listingIds }),
        ...(bundleIds.length > 0 ? { bundle_ids: bundleIds } : {}),
      };
      try {
        const res = await apiClient.post("/listings/change/batch_price/", body);
        if (!res.ok) {
          handleErrors(res.data);
          return;
        }
        const count =
          (isSelectedAll ? mockupsCount : selectedListingIds.length) + selectedBundleIds.length;
        toast.success(
          folderId == null ? `Moved ${count} item(s) to root.` : `Moved ${count} item(s) to folder.`,
        );
        resetMoveToFolderModalState();
        handleBatchPriceSuccess();
      } catch (e) {
        toast.error("Failed to move to folder");
      }
    });
  };

  const handleBackToRoot = () => {
    setIsEditingFolderName(false);
    setEditingFolderName("");
    setCurrentFolderId(null);
  };
  const handleFolderClick = (folderId) => {
    setIsEditingFolderName(false);
    setEditingFolderName("");
    setCurrentFolderId(folderId);
  };
  const currentFolder = shopFolders.find((f) => f.id === currentFolderId);

  const handleStartRenameFolder = () => {
    if (!currentFolder) return;
    setEditingFolderName(currentFolder.name || "");
    setIsEditingFolderName(true);
  };

  const handleCancelRenameFolder = () => {
    setIsEditingFolderName(false);
    setEditingFolderName("");
  };

  const handleSaveFolderName = async () => {
    if (!currentFolderId) return;
    const name = (editingFolderName || "").trim();
    if (!name) {
      toast.info("Enter a folder name");
      return;
    }
    if (name === currentFolder?.name) {
      handleCancelRenameFolder();
      return;
    }
    setRenamingFolder(true);
    try {
      await foldersApi.update(currentFolderId, { name });
      setShopFolders((prev) =>
        prev.map((folder) =>
          folder.id === currentFolderId ? { ...folder, name } : folder
        )
      );
      setIsEditingFolderName(false);
      setEditingFolderName("");
      toast.success("Folder renamed");
    } catch (e) {
      toast.error(e?.message || "Failed to update folder");
    } finally {
      setRenamingFolder(false);
    }
  };

  const handleDeleteFolderClick = () => {
    if (currentFolderId) setShowDeleteFolderWarning(true);
    else toast.error("No folder selected");
  };

  const handleDeleteFolderConfirm = async () => {
    setShowDeleteFolderWarning(false);
    if (!currentFolderId) return;
    try {
      await foldersApi.delete(currentFolderId);
      toast.success("Folder deleted. Listings are unassigned.");
      setCurrentFolderId(null);
      loadFolders();
      refetchGrid();
    } catch (e) {
      toast.error(e?.message || "Failed to delete folder");
    }
  };

  const modalClose = () => setShow(false);
  const bundleModalClose = () => setBundleShow(false);
  const modalCloseRemove = () => setShowRemove(false);
  const publishModalClose = () => setShowPublishModal(false);

  return (
    <>
      <Loader loading={bundleEditLoading} />
      {!showBundleListings ? (
        <>
          <section>
            <div className="container">
              <div className="row">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 my-listings-page-header">
                  <div className="d-flex align-items-center gap-3 mb-2 flex-grow-1 min-w-0">
                    {currentFolderId && (
                      <div style={{ cursor: "pointer" }}>
                        <TbArrowBackUp
                          onClick={handleBackToRoot}
                          style={{ fontSize: "32px" }}
                          className="shadow me-2 rounded-5 p-1 bg-light"
                        />
                      </div>
                    )}
                    {currentFolderId && isEditingFolderName ? (
                      <div className="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
                        <input
                          type="text"
                          className="form-control"
                          style={{ maxWidth: 320 }}
                          value={editingFolderName}
                          onChange={(e) => setEditingFolderName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSaveFolderName();
                            }
                            if (e.key === "Escape") handleCancelRenameFolder();
                          }}
                          disabled={renamingFolder}
                          autoFocus
                          aria-label="Folder name"
                        />
                        <button
                          type="button"
                          className="btn red-btn"
                          onClick={handleSaveFolderName}
                          disabled={renamingFolder}
                        >
                          {renamingFolder ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          className="btn grey-btn"
                          onClick={handleCancelRenameFolder}
                          disabled={renamingFolder}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <h2 className="mb-0" style={{ color: "#000000e6" }}>
                        {currentFolderId
                          ? currentFolder?.name || "Folder"
                          : "My Listings"}
                      </h2>
                    )}
                  </div>
                  {currentFolderId && !isEditingFolderName && (
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <button
                        type="button"
                        className="btn grey-btn"
                        onClick={handleStartRenameFolder}
                      >
                        Edit name
                      </button>
                      <button
                        type="button"
                        className="btn red-btn"
                        onClick={handleDeleteFolderClick}
                      >
                        Delete folder
                      </button>
                    </div>
                  )}
                </div>
                <div className="col-12 col-md-6 col-lg-5 mb-2">
                  <SearchBar
                    search={search}
                    setSearch={setSearch}
                    appliedSearch={searchQuery}
                    onApply={handleApplySearch}
                  />
                </div>
              </div>
            </div>
          </section>

          <div>
            <div className="container">
              {/* Shop folders list - only when not inside a folder */}
              {!currentFolderId && shopFolders.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3" style={{ color: "#000000e6" }}>
                    Shop folders
                  </h5>
                  <div className="row">
                    {shopFolders.map((folder) => (
                      <div
                        key={folder.id}
                        className="col-lg-3  col-6 mb-3"
                      >
                        <div
                          className="card p-3 shadow rounded-tr-lg"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s",
                            border: "2px solid transparent",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.02)";
                            e.currentTarget.style.borderColor = "#C93312";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.borderColor = "transparent";
                          }}
                          onClick={() => handleFolderClick(folder.id)}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <FaFolder style={{ fontSize: "48px", color: "#C93312" }} />
                            <div>
                              <h6 className="mb-0 fw-bold">{folder.name}</h6>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="my-listings-toolbar">
                <div className="my-listings-toolbar__controls">
                  <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />
                  {viewMode === "grid" && (
                    <div className="my-listings-toolbar__selection">
                      {!selectionModeActive ? (
                        <button
                          type="button"
                          className="btn bg-black text-white"
                          style={{ padding: "6px 36px" }}
                          onClick={() => setSelectionModeActive(true)}
                        >
                          Select
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn bg-black text-white"
                            style={{ padding: "6px 36px" }}
                            onClick={() => setSelectionModeActive(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className={`btn ${selectAllActive ? "" : "red-btn"} text-white`}
                            style={{
                              padding: "6px 36px",
                              ...(selectAllActive
                                ? {
                                    backgroundColor: "#1a1a1a",
                                    border: "2px solid #C93312",
                                    boxShadow:
                                      "inset 0 0 0 1px rgba(255,255,255,0.15)",
                                    fontWeight: 600,
                                  }
                                : {}),
                            }}
                            onClick={handleSelectAll}
                            disabled={
                              currentFolderId
                                ? !currentMockupIds?.length
                                : !mockupsCount
                            }
                            aria-pressed={selectAllActive}
                          >
                            {selectAllActive ? "All selected" : "Select all"}
                          </button>
                          <button
                            type="button"
                            className="btn red-btn"
                            onClick={handleUnselectAll}
                          >
                            Unselect all
                          </button>
                          {(isSelectedAll ||
                            selectedListingIds.length > 0 ||
                            selectedBundleIds.length > 0) && (
                            <>
                              <button
                                type="button"
                                className="btn red-btn"
                                onClick={() => setShowBatchPriceModal(true)}
                              >
                                Edit Price(
                                {(isSelectedAll
                                  ? mockupsCount
                                  : selectedListingIds.length) +
                                  selectedBundleIds.length}
                                )
                              </button>
                              <button
                                type="button"
                                className="btn red-btn"
                                onClick={() => {
                                  setMoveModalBatchMode(true);
                                  setSelectedMoveDestination(undefined);
                                  setShowMoveToFolderModal(true);
                                }}
                              >
                                Move
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {!currentFolderId && !selectionModeActive && (
                  <div className="my-listings-toolbar__actions">
                    <button
                      type="button"
                      className="btn red-btn my-listings-toolbar__btn--action"
                      onClick={handleModal}
                      disabled={loader}
                    >
                      Add Listing
                    </button>
                    <button
                      type="button"
                      className="btn red-btn my-listings-toolbar__btn--action"
                      onClick={handleBundleModal}
                      disabled={loader}
                    >
                      Add Bundle
                    </button>
                  </div>
                )}
              </div>

              <div className="my-listings mt-5 mb-5">
                {loader && records?.length === 0 && detailedRecords?.length === 0 ? (
                  <div className="d-flex justify-content-center mt-5">
                    <LoadingSpinner />
                  </div>
                ) : records?.length > 0 || detailedRecords?.length > 0 ? (
                  showGridView ? (
                    <InfiniteScroll
                      onLoadMore={gridLoadMoreVal}
                      hasMore={gridHasMoreVal}
                      loading={gridLoaderVal}
                    >
                      <div className="row">
                        {records?.map((item) => (
                          <GridListingCard
                            key={isBundleListing(item) ? `bundle-${item.id}` : `listing-${item.id}`}
                            item={item}
                            isLargeScreen={isLargeScreen}
                            menuAnchor={menuAnchor}
                            selectedItemId={selectedItemId}
                            selectionModeActive={selectionModeActive}
                            selectedListingIds={selectedListingIds}
                            selectedBundleIds={selectedBundleIds}
                            isSelectedAll={isSelectedAll}
                            onToggleSelect={handleToggleSelect}
                            onMenuClick={handleDropDownClick}
                            onMenuClose={handleDropDownClose}
                            onEdit={handleEditItem}
                            onDelete={openDeleteModal}
                            onOpenBundle={handleOpenBundle}
                            onSharableLink={handleSharableLink}
                            onPublishModal={openPublishModal}
                            showMoveToFolder={true}
                            onMoveToFolderClick={handleMoveToFolder}
                            showRemoveFromFolder={!!currentFolderId}
                            onRemoveFromFolderClick={removeFromFolder}
                            openBundleAsLink={false}
                          />
                        ))}
                      </div>
                    </InfiniteScroll>
                  ) : (
                    <div className="detailed-view">
                      <div className="my-listings-sort-row mb-3">
                        <SortDropdown
                          sortBy={detailedSortBy}
                          setSortBy={setDetailedSortBy}
                        />
                      </div>
                      <InfiniteScroll
                        onLoadMore={detailedLoadMore}
                        hasMore={detailedHasMore}
                        loading={detailedLoader}
                      >
                      {detailedRecords?.map((item) => (
                        <DetailedListingCard
                          key={isBundleListing(item) ? `bundle-${item.id}` : `listing-${item.id}`}
                          item={item}
                          menuAnchor={menuAnchor}
                          selectedItemId={selectedItemId}
                          selectionModeActive={selectionModeActive}
                          selectedListingIds={selectedListingIds}
                          selectedBundleIds={selectedBundleIds}
                          isSelectedAll={isSelectedAll}
                          onToggleSelect={handleToggleSelect}
                          onMenuClick={handleDropDownClick}
                          onMenuClose={handleDropDownClose}
                          onEdit={handleEditItem}
                          onDelete={openDeleteModal}
                          onOpenBundle={handleOpenBundle}
                          onSharableLink={handleSharableLink}
                          onPublishModal={openPublishModal}
                          showMoveToFolder={true}
                          onMoveToFolderClick={handleMoveToFolder}
                          showRemoveFromFolder={!!currentFolderId}
                          onRemoveFromFolderClick={
                            currentFolderId ? removeFromFolder : undefined
                          }
                          openBundleAsLink={false}
                        />
                      ))}
                      </InfiniteScroll>
                    </div>
                  )
                ) : (
                  <NotFound hasActiveSearch={Boolean(searchQuery?.trim())} />
                )}
              </div>

              <BundleModal
                show={bundleShow}
                onHide={bundleModalClose}
                editlistdata={editlistdata}
                mocko_id={mocko_id}
                fetchBundleMockups={fetchBundleMockups}
                FeaturedMockups={records}
              />
              <ListingModal
                show={show}
                onHide={modalClose}
                editlistdata={editlistdata}
                mocko_id={mocko_id}
                fetchFeaturedMockups={fetchFeaturedMockups}
                FeaturedMockups={records}
              />
            </div>

            <BatchPriceModal
              show={showBatchPriceModal}
              onHide={() => setShowBatchPriceModal(false)}
              selectedIds={selectedListingIds}
              selectedBundleIds={selectedBundleIds}
              isSelectedAll={isSelectedAll}
              mockupsCount={mockupsCount}
              onSuccess={handleBatchPriceSuccess}
            />
            <DeleteModal
              showRemove={showRemove}
              modalCloseRemove={modalCloseRemove}
              mocko_id={mocko_id}
              fetchFeaturedMockups={fetchFeaturedMockups}
              isBundleRemove={isBundleRemove}
              fetchBundleMockups={fetchBundleMockups}
              viewMode={viewMode}
              fetchAllDetails={refetchDetailed}
            />
            <WarningModal
              showModal={showDeleteFolderWarning}
              modalClose={() => setShowDeleteFolderWarning(false)}
              setConfirmClose={handleDeleteFolderConfirm}
              text="Delete this folder? Listings in it will be unassigned and stay in My Listings."
            />
            <Modal
              show={showMoveToFolderModal}
              onHide={closeMoveToFolderModal}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  {moveModalBatchMode ? "Move selected to folder" : "Move to folder"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {currentFolderId && (
                  <div className="list-group mb-3">
                    <button
                      type="button"
                      className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                      disabled={moveToFolderLoading}
                      onClick={() => setSelectedMoveDestination("root")}
                      style={{
                        cursor: moveToFolderLoading ? "not-allowed" : "pointer",
                        border:
                          selectedMoveDestination === "root"
                            ? "2px solid #C93312"
                            : "1px solid #dee2e6",
                        marginBottom: "8px",
                        borderRadius: "8px",
                        backgroundColor:
                          selectedMoveDestination === "root" ? "#fff5f2" : "white",
                        color: "#000",
                      }}
                    >
                      <FaFolder style={{ fontSize: "24px", color: "#6c757d" }} />
                      <div className="flex-grow-1 text-start">
                        <h6 className="mb-0" style={{ color: "#000" }}>
                          Root
                        </h6>
                        <small className="text-muted">
                          Move out of folder to My Listings root
                        </small>
                      </div>
                    </button>
                  </div>
                )}
                {shopFolders.length === 0 && !currentFolderId ? (
                  <p className="text-muted mb-0">
                    No folders yet. Create folders in My Shop first.
                  </p>
                ) : (
                  <div className="list-group">
                    {shopFolders.map((folder) => {
                      const isSelected = selectedMoveDestination === folder.id;
                      return (
                        <button
                          key={folder.id}
                          type="button"
                          className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                          disabled={moveToFolderLoading}
                          onClick={() => setSelectedMoveDestination(folder.id)}
                          style={{
                            cursor: moveToFolderLoading ? "not-allowed" : "pointer",
                            border: isSelected
                              ? "2px solid #C93312"
                              : "1px solid #dee2e6",
                            marginBottom: "8px",
                            borderRadius: "8px",
                            backgroundColor: isSelected ? "#fff5f2" : "white",
                            color: "#000",
                          }}
                        >
                          <FaFolder style={{ fontSize: "24px", color: "#C93312" }} />
                          <div className="flex-grow-1 text-start">
                            <h6 className="mb-0" style={{ color: "#000" }}>
                              {folder.name}
                            </h6>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={moveToFolderLoading}
                  onClick={closeMoveToFolderModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn red-btn"
                  disabled={
                    moveToFolderLoading ||
                    selectedMoveDestination === undefined ||
                    (shopFolders.length === 0 && !currentFolderId)
                  }
                  onClick={handleConfirmMoveToFolder}
                >
                  {moveToFolderLoading ? "Moving..." : "Confirm"}
                </button>
              </Modal.Footer>
            </Modal>
            <ModalPublishUnPublish
              showModal={showPublishModal}
              modalClose={publishModalClose}
              mocko_id={publishUnPublishMocko_id}
              fetchFeaturedMockups={fetchFeaturedMockups}
              publishValue={publishValue}
              fetchBundleMockups={fetchBundleMockups}
              isUnblockRequest={isUnblockRequest}
              isBundle={isBundle}
              isLinkEnabled={isSharableLinkEnabled}
              isDisableEnableLinkRequest={isSharableLinkRequest}
              viewMode={viewMode}
              fetchAllDetails={refetchDetailed}
            />
          </div>
        </>
      ) : (
        <>
          <BundleListingsView
            bundleListingsById={bundleListingsById}
            loader={loader}
            onBack={() => setShowBundleListings(false)}
            onEdit={handleBundleEdit}
          />
          <BatchPriceModal
            show={showBatchPriceModal}
            onHide={() => setShowBatchPriceModal(false)}
            selectedIds={selectedListingIds}
            selectedBundleIds={selectedBundleIds}
            isSelectedAll={isSelectedAll}
            mockupsCount={mockupsCount}
            onSuccess={handleBatchPriceSuccess}
          />
          <BundleModal
            show={bundleShow}
            onHide={bundleModalClose}
            editlistdata={editlistdata}
            mocko_id={mocko_id}
            fetchBundleMockups={fetchBundleMockups}
            FeaturedMockups={records}
          />
          <ListingModal
            show={show}
            onHide={modalClose}
            editlistdata={editlistdata}
            mocko_id={mocko_id}
            fetchFeaturedMockups={fetchFeaturedMockups}
            FeaturedMockups={records}
          />
          <DeleteModal
            showRemove={showRemove}
            modalCloseRemove={modalCloseRemove}
            mocko_id={mocko_id}
            fetchFeaturedMockups={fetchFeaturedMockups}
            isBundleRemove={isBundleRemove}
            fetchBundleMockups={fetchBundleMockups}
            viewMode={viewMode}
            fetchAllDetails={refetchDetailed}
          />
          <ModalPublishUnPublish
            showModal={showPublishModal}
            modalClose={publishModalClose}
            mocko_id={publishUnPublishMocko_id}
            fetchFeaturedMockups={fetchFeaturedMockups}
            publishValue={publishValue}
            fetchBundleMockups={fetchBundleMockups}
            isUnblockRequest={isUnblockRequest}
            isBundle={isBundle}
            isLinkEnabled={isSharableLinkEnabled}
            isDisableEnableLinkRequest={isSharableLinkRequest}
            viewMode={viewMode}
            fetchAllDetails={refetchDetailed}
          />
        </>
      )}
    </>
  );
};

export default MyListings;
